import { GlobalWizardState, LOBState } from "stores/wizard/state";
import { observable, action, computed } from "mobx";
import { CruiseConfig } from "stores/wizard/config";
import { TypeaheadSelection } from "uitk-react-typeahead";
import { Room, Travelers } from "src/components/shared/TravelersField/typings";
import { travelersMetadata } from "src/components/shared/TravelersField/utils";

export class CruiseWizardState implements LOBState {
  private globalState: GlobalWizardState;

  @computed
  public get canTrack() {
    return this.globalState.canTrack;
  }

  @computed
  public get location() {
    return this.globalState.location;
  }

  @computed
  public get travelersValueChanged() {
    return this.globalState.travelersValueChanged;
  }

  @action
  public setTravelersValue = () => {
    this.globalState.setTravelersValue();
  };

  @observable
  public config: CruiseConfig;

  @observable
  public subLOBState: string;

  //Since this LOB doesn't have a subLOB, setting this as empty so the queryParam is removed when navigating to this LOB
  @action
  public updateSubLOBState(subLOBState: string) {
    return false;
  }

  @computed
  public get date() {
    return this.globalState.date;
  }

  // other actions
  @action
  public overrideConfig(callback: () => void): void {
    callback();
  }

  @computed
  public get travelers() {
    const nonHotelRoom: [Room] = [
      {
        adults: 1,
        children: [],
        infants: [],
      },
    ];

    const applicableTravelerState = this.globalState.travelers.cruise;
    // Loop through existing room Info for travelers to find applicable room
    const applicableRoomsForTravelers: Room[] = applicableTravelerState.rooms.map((room: Room) => {
      const applicableAdultsPerRoom =
        room.adults > this.config.travelers.maxAdultsPerRoom ? this.config.travelers.maxAdultsPerRoom : room.adults;
      const combinedChildren = room.children.concat(room.infants);
      const applicableChildrenPerRoom =
        combinedChildren.length > this.config.travelers.maxChildrenPerRoom
          ? combinedChildren.splice(0, this.config.travelers.maxChildrenPerRoom)
          : combinedChildren;

      return {
        adults: applicableAdultsPerRoom,
        children: applicableChildrenPerRoom,
        infants: [],
      };
    });

    // Only the first room details to be preserved in nonHotel [Room]
    nonHotelRoom[0] = applicableRoomsForTravelers[0];

    const cruiseTravelers = {
      hotel: {
        rooms: applicableRoomsForTravelers,
        infantSeating: this.globalState.travelers.hotel.infantSeating,
      },
      hotelPackage: {
        rooms: applicableRoomsForTravelers,
        infantSeating: this.globalState.travelers.hotelPackage.infantSeating,
      },
      nonHotel: {
        rooms: nonHotelRoom,
        infantSeating: this.globalState.travelers.nonHotel.infantSeating,
      },
      cruise: {
        rooms: nonHotelRoom,
        infantSeating: this.globalState.travelers.cruise.infantSeating,
      },
    };

    return cruiseTravelers;
  }

  @computed
  public get hotelTravelersMetadata() {
    return travelersMetadata(this.travelers.hotel);
  }

  @computed
  public get nonHotelTravelersMetadata() {
    return travelersMetadata(this.travelers.nonHotel);
  }

  @computed
  public get cruiseTravelersMetadata() {
    return travelersMetadata(this.travelers.cruise);
  }

  @action
  public updateNonHotelTravelersSelection = (travelers: Travelers) => {
    this.globalState.updateNonHotelTravelersSelection(travelers);
  };

  @observable
  public cruiseDestination: string = "";

  @action
  public setCruiseDestination = (destination: string) => {
    this.cruiseDestination = destination;
  };

  @observable
  public dateStartInvalidKey: string = "";

  @observable
  public dateEndInvalidKey: string = "";

  @observable
  public destinationInvalidKey: string | "";

  @observable
  public nonHotelTravelersInvalidKey: string = "";

  @action
  public validateLessThanNTravelers = () => {
    if (!this.globalState.validateLessThanNTravelers(this.nonHotelTravelersMetadata, this.config.travelers)) {
      this.nonHotelTravelersInvalidKey = this.config.travelers.invalidLessThanNTravelersMessageToken;

      return false;
    }

    return true;
  };

  @action
  public updateDateSelection = (start: Date, end: Date) => {
    this.globalState.updateDateSelection(start, end);
  };

  @action
  public updateDestinationSelection = (selection: TypeaheadSelection) => {
    this.globalState.updateDestinationSelection(selection);
  };

  @action
  public resetValidations = () => {
    this.nonHotelTravelersInvalidKey = "";

    return true;
  };

  @action
  public validateTravelersField = () => this.validateLessThanNTravelers();

  public validateForm = () => {
    const validations: boolean[] = [this.validateTravelersField()];

    return !validations.includes(false);
  };

  public submit = (event: React.FormEvent) => {
    const isValidForm = this.validateForm();
    if (!isValidForm) {
      event.preventDefault();
    }
    this.globalState.submit();
  };

  constructor(config: CruiseConfig, globalState: GlobalWizardState) {
    this.config = config;
    this.globalState = globalState;
  }
}
